import { useState } from 'react';
import { isSpecialRole, type Role } from '@bm/shared';
import { de } from '@/i18n/de';

interface RoleCardProps {
  role: Role | null;
  /** Namen der Mitverschwörer — nur für MAFIA relevant. */
  colleagueNames: string[];
}

/**
 * Volle Klassennamen statt zusammengesetzter: Tailwind scannt den Quelltext und
 * findet berechnete Klassen nicht.
 */
const TONE = {
  mafia: { border: 'border-mafia/50', bg: 'bg-mafia-muted', text: 'text-mafia' },
  citizen: { border: 'border-citizen/50', bg: 'bg-citizen-muted', text: 'text-citizen' },
  special: { border: 'border-accent/50', bg: 'bg-accent-muted', text: 'text-accent' },
} as const;

const ROLE_FACE = {
  CITIZEN: { emoji: '🏘️', tone: 'citizen' },
  MAFIA: { emoji: '☠️', tone: 'mafia' },
  BLESSING: { emoji: de.specialRoles.BLESSING.emoji, tone: 'special' },
  VOTE_BLOCK: { emoji: de.specialRoles.VOTE_BLOCK.emoji, tone: 'special' },
  DEAD_REDEMPTION: { emoji: de.specialRoles.DEAD_REDEMPTION.emoji, tone: 'special' },
  DOUBLE_UP: { emoji: de.specialRoles.DOUBLE_UP.emoji, tone: 'special' },
} as const satisfies Record<Role, { emoji: string; tone: keyof typeof TONE }>;

/**
 * Hold-to-Reveal: Die Karte zeigt die Rolle ausschließlich, solange der
 * Finger/die Maustaste gehalten wird — Schutz vor Mitlesen am Tisch.
 * Loslassen dreht sofort zurück (kein Timeout, kein zweiter Tap nötig).
 *
 * `touch-action: none` und `setPointerCapture` gehören zwingend zusammen mit
 * dieser Geste: Ohne sie deutet der Browser die kleinste Fingerbewegung als
 * Scroll um, feuert `pointercancel` — und die Karte klappt genau dann zu, wenn
 * man sie ruhig halten will.
 *
 * Geteilt zwischen Online-Spiel (eigene Rolle) und Ein-Gerät-Modus (Rolle des
 * Spielers, der das Handy gerade hält).
 */
export function RoleCard({ role, colleagueNames }: RoleCardProps) {
  const [revealed, setRevealed] = useState(false);
  const isMafia = role === 'MAFIA';
  const face = ROLE_FACE[role ?? 'CITIZEN'];
  const tone = TONE[face.tone];

  const show = () => setRevealed(true);
  const hide = () => setRevealed(false);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={revealed}
      aria-label={de.roleReveal.holdToReveal}
      onPointerDown={(event) => {
        show();
        // Der Zeiger darf über den Kartenrand hinauswandern, ohne dass die
        // Karte zuklappt — sonst reicht ein Rutscher zum Rand. Erst aufdecken,
        // dann greifen: `setPointerCapture` wirft, wenn der Zeiger inzwischen
        // weg ist, und das Aufdecken darf daran nicht scheitern.
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // Ohne Capture bleibt es beim Verhalten von vorher — kein Fehlerfall.
        }
      }}
      onPointerUp={hide}
      onPointerCancel={hide}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') show();
      }}
      onKeyUp={(event) => {
        if (event.key === 'Enter' || event.key === ' ') hide();
      }}
      className="relative aspect-[3/4] w-full max-w-[280px] cursor-pointer touch-none select-none"
      style={{ perspective: '1200px' }}
    >
      <div
        className="relative h-full w-full transition-transform duration-300 ease-out"
        style={{
          transformStyle: 'preserve-3d',
          transform: revealed ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Rückseite */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-3xl border border-surface-border bg-surface-2"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <span className="font-display text-5xl">🂠</span>
          <p className="px-6 text-center text-sm text-fg-muted">{de.roleReveal.holdToReveal}</p>
        </div>

        {/* Vorderseite */}
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-3xl border px-6 text-center ${tone.border} ${tone.bg}`}
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <span className="text-5xl" aria-hidden="true">
            {face.emoji}
          </span>
          <p className="font-display text-sm font-semibold tracking-wide text-fg-muted">
            {de.roleReveal.youAre}
          </p>
          <p
            className={`font-display font-extrabold uppercase ${tone.text} ${
              face.tone === 'special' ? 'text-3xl' : 'text-4xl'
            }`}
          >
            {de.roles[role ?? 'CITIZEN']}
          </p>
          {isMafia ? (
            colleagueNames.length > 0 ? (
              <div className="mt-1">
                <p className="mb-1 text-sm text-fg-muted">{de.roleReveal.mafiaHint}</p>
                <ul className="text-sm font-medium">
                  {colleagueNames.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-fg-muted">{de.roleReveal.mafiaSoloHint}</p>
            )
          ) : (
            <p className="text-sm text-fg-muted">
              {role && isSpecialRole(role)
                ? de.specialRoles[role].cardHint
                : de.roleReveal.citizenHint}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
